import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InviteRepDialog } from "@/components/InviteRepDialog";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Trophy, Loader2, Crown, Swords, Users, Clock, ChevronRight, Medal, GitFork, Play, Flag, UserPlus
} from "lucide-react";

function daysLeft(iso) {
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d > 0 ? `${d}d left` : "Ended";
}

function MatchSlot({ slot, isWinner }) {
  return (
    <div className={`flex items-center justify-between px-3 py-1.5 text-xs rounded-md ${isWinner ? "bg-emerald-500/15 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
      <span className="truncate">{slot ? slot.name : "TBD"}</span>
      <span className="font-mono ml-2">{slot?.played ? slot.rating : slot ? "…" : ""}</span>
    </div>
  );
}

function Bracket({ rounds }) {
  return (
    <div className="flex gap-4 overflow-x-auto p-4">
      {rounds.map((rnd, ri) => (
        <div key={ri} className="flex flex-col justify-around gap-3 min-w-[160px]">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
            {ri === rounds.length - 1 ? "Final" : `Round ${ri + 1}`}
          </div>
          {rnd.map((m) => (
            <div key={m.id} className="card-surface rounded-lg p-2 space-y-1 border border-slate-200" data-testid={`bracket-match-${m.id}`}>
              <MatchSlot slot={m.p1} isWinner={m.winner_user_id && m.p1 && m.winner_user_id === m.p1.user_id} />
              <MatchSlot slot={m.p2} isWinner={m.winner_user_id && m.p2 && m.winner_user_id === m.p2.user_id} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Tournaments() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [board, setBoard] = useState(null);
  const [bracketData, setBracketData] = useState({}); // tid -> bracket payload
  const [busy, setBusy] = useState(null);
  const [invite, setInvite] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  const load = async () => {
    let data;
    try { ({ data } = await api.get("/tournaments")); } catch (err) { console.warn("[Tournaments] request failed", err); setTournaments([]); return; }
    setTournaments(data);
    // preload bracket meta for bracket tournaments
    const brackets = {};
    await Promise.all(data.filter((t) => t.format === "bracket").map(async (t) => {
      try { brackets[t.id] = (await api.get(`/tournaments/${t.id}/bracket`)).data; } catch (err) { console.warn("[Tournaments] ignored error", err); }
    }));
    setBracketData(brackets);
  };
  useEffect(() => { load(); }, []);

  // Invite link: /app/tournaments?join=<tournament id>[&team=<team code>]
  useEffect(() => {
    const tid = searchParams.get("join"); const teamCode = searchParams.get("team");
    if (!tid) return;
    (async () => {
      if (teamCode) { try { await api.post("/teams/join", { code: teamCode }); toast.success("You've joined the team!"); refreshUser(); } catch (e) { if (e.response?.status !== 400) toast.error(e.response?.data?.detail || "Couldn't join the team"); } }
      try { await api.post(`/tournaments/${tid}/join`); toast.success("You're in the tournament!"); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't enter the tournament"); }
      setSearchParams({}, { replace: true }); await load();
    })();
  }, [searchParams]); // eslint-disable-line

  const join = async (t) => {
    setBusy(t.id);
    try { await api.post(`/tournaments/${t.id}/join`); toast.success(`Entered ${t.name}!`); await load(); }
    catch { toast.error("Couldn't join"); } finally { setBusy(null); }
  };
  const startBracket = async (t) => {
    setBusy(t.id);
    try { await api.post(`/tournaments/${t.id}/start`); toast.success("Bracket started!"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start"); } finally { setBusy(null); }
  };
  const viewBoard = async (t) => {
    if (openId === t.id) { setOpenId(null); return; }
    setOpenId(t.id); setBoard(null);
    const { data } = await api.get(`/tournaments/${t.id}/leaderboard`);
    setBoard(data.leaderboard);
  };
  const playScore = (t) => navigate(`/app/roleplay?tournament=${t.id}&persona=${t.persona_id}`);
  const playMatch = (t, matchId) => navigate(`/app/roleplay?tournament=${t.id}&persona=${t.persona_id}&match=${matchId}`);

  if (!tournaments) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <InviteRepDialog tournament={invite} onClose={() => setInvite(null)} />
      <div>
        <p className="text-amber-600 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><Trophy className="w-4 h-4" /> Compete</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Tournaments</h1>
        <p className="text-slate-500 mt-2">Score cups rank everyone on their best run. Knockout brackets pit reps head-to-head until one champion remains.</p>
      </div>

      {tournaments.length === 0 && <div className="card-surface rounded-2xl p-8 text-center text-slate-500">No tournaments running right now. Check back soon.</div>}

      <div className="space-y-4">
        {tournaments.map((t) => {
          const isBracket = t.format === "bracket";
          const bd = bracketData[t.id] || {};
          const champion = t.champion;
          return (
            <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-2xl overflow-hidden" data-testid={`tournament-${t.id}`}>
              <div className="flex items-center gap-4 p-5">
                <img src={t.persona_avatar} alt="" className="w-14 h-14 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-bold text-slate-900 text-lg">{t.name}</h2>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${isBracket ? "border-violet-500/30 bg-violet-500/10 text-violet-600" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"}`}>
                      {isBracket ? <span className="flex items-center gap-1"><GitFork className="w-3 h-3" /> Knockout</span> : "Score cup"}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border border-slate-200 text-slate-500">{t.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Swords className="w-3 h-3" /> {t.persona_title}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {isBracket ? (bd.entries ?? 0) : t.entries} in</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {daysLeft(t.ends_at)}</span>
                    {!isBracket && t.my_best != null && <span className="text-emerald-600">Your best: {t.my_best}</span>}
                  </div>
                </div>
              </div>

              {champion && (
                <div className="mx-5 mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2 text-amber-800 text-sm font-semibold" data-testid={`champion-${t.id}`}>
                  <Crown className="w-4 h-4" /> Champion: {champion.name}
                </div>
              )}

              <div className="px-5 pb-5 flex flex-wrap gap-2">
                {t.status !== "completed" && (
                  <Button onClick={() => setInvite(t)} variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 font-semibold" data-testid={`invite-rep-${t.id}`}>
                    <UserPlus className="w-4 h-4 mr-1" /> Invite rep
                  </Button>
                )}
                {isBracket ? (
                  <>
                    {t.status === "open" && !bd.joined && (
                      <Button onClick={() => join(t)} disabled={busy === t.id} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid={`join-tournament-${t.id}`}>
                        {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter bracket"}
                      </Button>
                    )}
                    {bd.can_start && (
                      <Button onClick={() => startBracket(t)} disabled={busy === t.id} className="bg-violet-500 hover:bg-violet-600 text-white font-semibold" data-testid={`start-bracket-${t.id}`}>
                        <Flag className="w-4 h-4 mr-1" /> Start bracket
                      </Button>
                    )}
                    {t.status === "active" && bd.my_match_id && (
                      <Button onClick={() => playMatch(t, bd.my_match_id)} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid={`play-match-${t.id}`}>
                        <Play className="w-4 h-4 mr-1" /> Play my match
                      </Button>
                    )}
                    {t.status === "active" && bd.joined && !bd.my_match_id && !champion && (
                      <span className="text-sm text-slate-500 flex items-center gap-2 px-2 py-2"><Clock className="w-4 h-4" /> Waiting for your next match…</span>
                    )}
                  </>
                ) : (
                  <>
                    {!t.joined ? (
                      <Button onClick={() => join(t)} disabled={busy === t.id} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid={`join-tournament-${t.id}`}>
                        {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter tournament"}
                      </Button>
                    ) : (
                      <Button onClick={() => playScore(t)} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid={`play-tournament-${t.id}`}>
                        <Play className="w-4 h-4 mr-1" /> Play my round
                      </Button>
                    )}
                    <Button onClick={() => viewBoard(t)} variant="outline" className="border-slate-300 bg-transparent text-slate-900 hover:bg-slate-100" data-testid={`board-tournament-${t.id}`}>
                      <Medal className="w-4 h-4 mr-1" /> Champions <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${openId === t.id ? "rotate-90" : ""}`} />
                    </Button>
                  </>
                )}
              </div>

              {isBracket && bd.tournament?.rounds && bd.tournament.rounds.length > 0 && (
                <div className="border-t border-slate-200">
                  <Bracket rounds={bd.tournament.rounds} />
                </div>
              )}

              {!isBracket && openId === t.id && (
                <div className="border-t border-slate-200">
                  {!board ? <div className="p-6 text-center text-emerald-600"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
                    board.length === 0 ? <div className="p-6 text-center text-slate-500 text-sm">No scores yet — be the first champion.</div> :
                    board.map((e) => (
                      <div key={e.user_id} className={`flex items-center gap-4 px-5 py-3 border-b border-slate-200 last:border-0 ${e.is_me ? "bg-emerald-500/10" : ""}`} data-testid={`tboard-row-${e.rank}`}>
                        <div className="w-8 flex justify-center">{e.rank <= 3 ? <Crown className={`w-4 h-4 ${e.rank === 1 ? "text-amber-600" : e.rank === 2 ? "text-slate-600" : "text-orange-600"}`} /> : <span className="text-sm font-mono text-slate-500">{e.rank}</span>}</div>
                        <div className="flex-1 font-medium text-slate-900 flex items-center gap-2">{e.name}{e.is_me && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-black font-bold">YOU</span>}</div>
                        <div className="font-mono font-bold text-emerald-600">{e.best_rating}</div>
                      </div>
                    ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
